/**
 * Configurazione dell'indice di ricerca
 * Questo file contiene tutti i contenuti ricercabili del sito
 */

import {
  Home,
  Users,
  Calendar,
  Castle,
  BookOpen,
  FileText,
  Shield,
  Award,
  Heart,
  Lock,
  FileCheck,
  Bike,
  Footprints,
  Mountain,
  LucideIcon,
} from "lucide-react";

// ============================================
// TIPI
// ============================================

export type SearchCategory = "pages" | "castelli" | "attivita";

export interface SearchItem {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  category: SearchCategory;
  path: string;
  icon?: LucideIcon;
  image?: string;
  meta?: {
    date?: string;
    location?: string;
    type?: string;
  };
}

// ============================================
// PAGINE DEL SITO
// ============================================

export const SEARCH_PAGES: SearchItem[] = [
  {
    id: "home",
    title: "Home",
    description: "Pagina principale del sito ASD 99 Castelli",
    keywords: ["home", "principale", "benvenuto", "99 castelli", "aquila"],
    category: "pages",
    path: "/",
    icon: Home,
  },
  {
    id: "associazione",
    title: "Chi Siamo",
    description: "Storia, missione e valori dell'ASD 99 Castelli",
    keywords: ["chi siamo", "associazione", "storia", "alessandro serreli", "fondatore", "missione", "valori"],
    category: "pages",
    path: "/associazione",
    icon: Users,
  },
  {
    id: "tesseramento",
    title: "Tesseramento 2026",
    description: "Diventa socio dell'ASD 99 Castelli - Quote e modalità di iscrizione",
    keywords: ["tesseramento", "iscrizione", "socio", "quota", "tessera", "csi", "2026", "prezzo"],
    category: "pages",
    path: "/tesseramento",
    icon: FileCheck,
  },
  {
    id: "attivita",
    title: "Attività ed Eventi",
    description: "Calendario delle escursioni, pedalate e visite culturali",
    keywords: ["attività", "eventi", "escursioni", "pedalate", "calendario", "programma"],
    category: "pages",
    path: "/attivita",
    icon: Calendar,
  },
  {
    id: "castelli",
    title: "I 99 Castelli",
    description: "Mappa interattiva e schede dei castelli visitati",
    keywords: ["castelli", "mappa", "99", "fortezze", "torri", "rocche", "medievali"],
    category: "pages",
    path: "/castelli",
    icon: Castle,
  },
  {
    id: "laquila-cecilia",
    title: "L'Aquila Cecilia",
    description: "Libro per bambini sulla storia di L'Aquila - Download gratuito",
    keywords: ["aquila cecilia", "libro", "bambini", "storia", "download", "pdf", "gratis"],
    category: "pages",
    path: "/laquila-cecilia",
    icon: BookOpen,
  },
  {
    id: "documenti",
    title: "Documenti e Moduli",
    description: "Moduli di iscrizione, liberatorie e documenti ufficiali",
    keywords: ["documenti", "moduli", "iscrizione", "liberatoria", "privacy", "scarica"],
    category: "pages",
    path: "/documenti",
    icon: FileText,
  },
  {
    id: "trasparenza",
    title: "Trasparenza",
    description: "Statuto, codice fiscale e documenti ufficiali dell'associazione",
    keywords: ["trasparenza", "statuto", "codice fiscale", "documenti ufficiali"],
    category: "pages",
    path: "/trasparenza",
    icon: Shield,
  },
  {
    id: "attestati",
    title: "Attestati e Certificazioni",
    description: "Certificazioni CSI e qualifiche delle guide",
    keywords: ["attestati", "certificazioni", "csi", "guide", "qualifiche"],
    category: "pages",
    path: "/attestati",
    icon: Award,
  },
  {
    id: "amici",
    title: "Amici e Partner",
    description: "Partner e collaborazioni dell'ASD 99 Castelli",
    keywords: ["amici", "partner", "collaborazioni", "sponsor"],
    category: "pages",
    path: "/amici",
    icon: Heart,
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    description: "Informativa sulla privacy e trattamento dati",
    keywords: ["privacy", "gdpr", "dati", "cookie"],
    category: "pages",
    path: "/privacy",
    icon: Lock,
  },
  {
    id: "termini",
    title: "Termini di Servizio",
    description: "Condizioni d'uso del sito web",
    keywords: ["termini", "condizioni", "uso", "servizio"],
    category: "pages",
    path: "/termini",
    icon: FileText,
  },
];

// ============================================
// CASTELLI
// ============================================

export const SEARCH_CASTELLI: SearchItem[] = [
  {
    id: "castello-arischia",
    title: "Castello di Arischia",
    description: "Antico borgo fortificato del quarto di Santa Maria",
    keywords: ["arischia", "borgo", "santa maria"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Arischia, AQ" },
  },
  {
    id: "castello-assergi",
    title: "Castello di Assergi",
    description: "Città fortificata medievale ai piedi del Gran Sasso",
    keywords: ["assergi", "gran sasso", "medievale"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Assergi, AQ" },
  },
  {
    id: "castello-barete",
    title: "Castello di Barete",
    description: "Borgo medievale fondatore nel quarto di San Pietro",
    keywords: ["barete", "san pietro", "medievale"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Barete, AQ" },
  },
  {
    id: "castello-barili",
    title: "Castello di Barili",
    description: "Piccolo borgo medievale dell'aquilano",
    keywords: ["barili", "borgo", "medievale"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Barili, AQ" },
  },
  {
    id: "castello-bariscianello",
    title: "Castello di Bariscianello",
    description: "Frazione con resti di fortificazioni medievali",
    keywords: ["bariscianello", "fortificazioni"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Bariscianello, AQ" },
  },
  {
    id: "castello-barisciano",
    title: "Castello di Barisciano",
    description: "Castello-recinto del XII secolo",
    keywords: ["barisciano", "castello recinto", "xii secolo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Barisciano, AQ" },
  },
  {
    id: "castello-beffi",
    title: "Castello di Beffi",
    description: "Borgo fortificato con torre pentagonale",
    keywords: ["beffi", "torre pentagonale", "fortificato"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Beffi, AQ" },
  },
  {
    id: "castello-bominaco",
    title: "Castello di Bominaco",
    description: "Castello-recinto con Oratorio di San Pellegrino, la Cappella Sistina d'Abruzzo",
    keywords: ["bominaco", "san pellegrino", "cappella sistina", "affreschi"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Bominaco, AQ" },
  },
  {
    id: "castello-camarda",
    title: "Castello di Camarda",
    description: "Antico castello del quarto di Santa Maria",
    keywords: ["camarda", "santa maria", "antico"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Camarda, AQ" },
  },
  {
    id: "castello-caporciano",
    title: "Castello di Caporciano",
    description: "Borgo medievale ai margini dell'altopiano di Navelli",
    keywords: ["caporciano", "navelli", "medievale"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Caporciano, AQ" },
  },
  {
    id: "castello-cascina",
    title: "Castello di Cascina",
    description: "Castello fondatore dell'Aquila",
    keywords: ["cascina", "fondatore", "aquila"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Cascina, AQ" },
  },
  {
    id: "castello-ocre",
    title: "Castello di Ocre",
    description: "Spettacolare borgo fortificato con 470 m di mura e sette torri",
    keywords: ["ocre", "mura", "torri", "san salvatore"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Ocre, AQ" },
  },
  {
    id: "castello-castiglione",
    title: "Castello di Castiglione",
    description: "Borgo d'altura con origini medievali",
    keywords: ["castiglione", "altura", "medievale"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Castiglione, AQ" },
  },
  {
    id: "castello-civitaretenga",
    title: "Castello di Civitaretenga",
    description: "Torre duecentesca quadrangolare di 14 m con antico ghetto ebraico",
    keywords: ["civitaretenga", "torre", "ghetto ebraico"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Civitaretenga, AQ" },
  },
  {
    id: "castello-collepietro",
    title: "Castello di Collepietro",
    description: "Borgo fortificato feudo dei Gualtieri",
    keywords: ["collepietro", "gualtieri", "feudo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Collepietro, AQ" },
  },
  {
    id: "castello-coppito",
    title: "Castello di Coppito",
    description: "Sede della Basilica di Collemaggio fondata da Papa Celestino V",
    keywords: ["coppito", "collemaggio", "celestino v", "basilica"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Coppito, AQ" },
  },
  {
    id: "castello-corno",
    title: "Castello di Corno",
    description: "Antico castello del contado di Antrodoco",
    keywords: ["corno", "antrodoco", "antico"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Corno, AQ" },
  },
  {
    id: "castello-fagnano",
    title: "Castello di Fagnano",
    description: "Borgo con torre circolare del XIII secolo",
    keywords: ["fagnano", "torre circolare", "xiii secolo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Fagnano Alto, AQ" },
  },
  {
    id: "castello-fontecchio",
    title: "Castello di Fontecchio",
    description: "Torre duecentesca con porta urbica originale",
    keywords: ["fontecchio", "torre", "porta urbica"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Fontecchio, AQ" },
  },
  {
    id: "castello-forcella",
    title: "Castello di Forcella",
    description: "Borgo fortificato dell'entroterra aquilano",
    keywords: ["forcella", "fortificato", "borgo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Forcella, AQ" },
  },
  {
    id: "castello-navelli",
    title: "Castello di Navelli",
    description: "Borgo sull'altopiano famoso per lo zafferano",
    keywords: ["navelli", "zafferano", "altopiano"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Navelli, AQ" },
  },
  {
    id: "castello-pizzoli",
    title: "Castello di Pizzoli",
    description: "Borgo del contado aquilano",
    keywords: ["pizzoli", "contado", "borgo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Pizzoli, AQ" },
  },
  {
    id: "castello-poggio-picenze",
    title: "Castello di Poggio Picenze",
    description: "Borgo medievale con resti di fortificazioni",
    keywords: ["poggio picenze", "medievale", "fortificazioni"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Poggio Picenze, AQ" },
  },
  {
    id: "castello-preturo",
    title: "Castello di Preturo",
    description: "Antico borgo del quarto di San Pietro",
    keywords: ["preturo", "san pietro", "borgo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Preturo, AQ" },
  },
  {
    id: "castello-san-pio",
    title: "Castello di San Pio delle Camere",
    description: "Borgo con importante torre di avvistamento",
    keywords: ["san pio", "camere", "torre"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "San Pio delle Camere, AQ" },
  },
  {
    id: "castello-stiffe",
    title: "Castello di Stiffe",
    description: "Borgo vicino alle famose Grotte di Stiffe",
    keywords: ["stiffe", "grotte", "borgo"],
    category: "castelli",
    path: "/castelli",
    icon: Castle,
    meta: { location: "Stiffe, AQ" },
  },
];

// ============================================
// ATTIVITÀ/EVENTI
// ============================================

export const SEARCH_ATTIVITA: SearchItem[] = [
  // 2025
  {
    id: "attivita-valle-interria-2025",
    title: "Valle Interria e Complesso di Sant'Antonio",
    description: "Escursione alla Valle Interria",
    keywords: ["valle interria", "sant antonio", "escursione", "dicembre", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "7-8 Dicembre 2025", location: "Valle Interria", type: "Escursione" },
  },
  {
    id: "attivita-collepietro-2025",
    title: "Castello di Collepietro",
    description: "Visita culturale al Castello di Collepietro",
    keywords: ["collepietro", "visita", "novembre", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "2 Novembre 2025", location: "Collepietro (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-piscignola-2025",
    title: "Castello di Piscignola e Rocca di Fondi",
    description: "Escursione a Piscignola e Rocca di Fondi",
    keywords: ["piscignola", "fondi", "rocca", "ottobre", "escursione", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "12 Ottobre 2025", location: "Piscignola - Fondi (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-family-2025",
    title: "Territorio in Bici - Family Experience",
    description: "Cicloturistica per famiglie",
    keywords: ["bici", "family", "famiglia", "bambini", "luglio", "cicloturistica", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "5 Luglio 2025", location: "L'Aquila e dintorni", type: "Cicloturistica" },
  },
  {
    id: "attivita-cicloturistica-2025",
    title: 'Cicloturistica "L\'Aquila Città Territorio"',
    description: "Seconda edizione della cicloturistica",
    keywords: ["cicloturistica", "aquila", "bici", "maggio", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "25 Maggio 2025", location: "L'Aquila", type: "Cicloturistica" },
  },
  {
    id: "attivita-castiglione-2025",
    title: "Castello di Castiglione di Tornimparte",
    description: "Visita al Castello di Castiglione",
    keywords: ["castiglione", "tornimparte", "visita", "maggio", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "18 Maggio 2025", location: "Tornimparte (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-san-marco-2025",
    title: "San Marco di Preturo",
    description: "Escursione a San Marco di Preturo",
    keywords: ["san marco", "preturo", "escursione", "aprile", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "26 Aprile 2025", location: "Preturo (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-cascina-2025",
    title: "Castello di Cascina",
    description: "Visita al Castello di Cascina",
    keywords: ["cascina", "visita", "aprile", "2025"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "6 Aprile 2025", location: "Cascina (AQ)", type: "Visita Culturale" },
  },
  // 2024
  {
    id: "attivita-forcella-2024",
    title: "Castello di Forcella",
    description: "Visita natalizia al Castello di Forcella",
    keywords: ["forcella", "natale", "dicembre", "visita", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "22 Dicembre 2024", location: "Forcella (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-san-benedetto-2024",
    title: "Castello di San Benedetto in Perillis",
    description: "Visita al Castello di San Benedetto in Perillis",
    keywords: ["san benedetto", "perillis", "visita", "novembre", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "30 Novembre 2024", location: "San Benedetto in Perillis (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-marrocchia-2024",
    title: "La Marrocchia, Non Solo Mais!",
    description: "Evento culturale sulla Marrocchia",
    keywords: ["marrocchia", "mais", "evento", "novembre", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "2 e 9 Novembre 2024", location: "L'Aquila", type: "Evento" },
  },
  {
    id: "attivita-castagna-2024",
    title: "Cicloturistica della Castagna",
    description: "Cicloturistica autunnale a Campotosto",
    keywords: ["castagna", "cicloturistica", "campotosto", "ottobre", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "27 Ottobre 2024", location: "Campotosto (AQ)", type: "Cicloturistica" },
  },
  {
    id: "attivita-citta-acqua-2024",
    title: 'Ciclo Escursione "La Città e l\'Acqua"',
    description: "Ciclo escursione alla scoperta dell'acqua a L'Aquila",
    keywords: ["citta", "acqua", "cicloturistica", "luglio", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "6 Luglio 2024", location: "L'Aquila", type: "Cicloturistica" },
  },
  {
    id: "attivita-barisciano-2024",
    title: "Castello di Barisciano",
    description: "Visita al Castello di Barisciano",
    keywords: ["barisciano", "castello", "visita", "giugno", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "29 Giugno 2024", location: "Barisciano (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-gran-sasso-2024",
    title: "Castelli del Gran Sasso",
    description: "Escursione ai castelli del Gran Sasso",
    keywords: ["gran sasso", "castelli", "escursione", "giugno", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "29 Giugno 2024", location: "Gran Sasso (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-fontecchio-2024",
    title: "Castello di Fontecchio",
    description: "Visita al Castello di Fontecchio",
    keywords: ["fontecchio", "castello", "visita", "maggio", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "25 Maggio 2024", location: "Fontecchio (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-aquila-bici-2024",
    title: "L'Aquila in Bici e Cicloturistica del Territorio",
    description: "Cicloturistica per le vie dell'Aquila",
    keywords: ["aquila", "bici", "cicloturistica", "maggio", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "19 Maggio 2024", location: "L'Aquila", type: "Cicloturistica" },
  },
  {
    id: "attivita-san-marco-2024",
    title: "San Marco di Preturo: Origini, Personaggi e Tradizioni",
    description: "Visita culturale a San Marco di Preturo",
    keywords: ["san marco", "preturo", "visita", "aprile", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "27 Aprile 2024", location: "Preturo (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-picenze-2024",
    title: "Castello di Picenze",
    description: "Visita al Castello di Picenze",
    keywords: ["picenze", "castello", "visita", "aprile", "2024"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "7 Aprile 2024", location: "Picenze (AQ)", type: "Visita Culturale" },
  },
  // 2023
  {
    id: "attivita-navelli-2023",
    title: "Castelli Piana di Navelli",
    description: "Escursione ai castelli della Piana di Navelli",
    keywords: ["navelli", "piana", "castelli", "escursione", "ottobre", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "28 Ottobre 2023", location: "Piana di Navelli (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-valle-vasto-2023",
    title: "Castelli della Valle del Vasto",
    description: "Escursione ai castelli della Valle del Vasto",
    keywords: ["valle", "vasto", "castelli", "escursione", "ottobre", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "1 Ottobre 2023", location: "Valle del Vasto (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-amiternum-2023",
    title: "Conosciamo Amiternum",
    description: "Visita all'antica città romana di Amiternum",
    keywords: ["amiternum", "romano", "visita", "settembre", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "3 Settembre 2023", location: "Amiternum (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-san-vittorino-2023",
    title: "Castello di San Vittorino",
    description: "Visita al Castello di San Vittorino",
    keywords: ["san vittorino", "castello", "visita", "agosto", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "18 Agosto 2023", location: "San Vittorino (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-preturo-2023",
    title: "Castello di Preturo",
    description: "Visita al Castello di Preturo",
    keywords: ["preturo", "castello", "visita", "agosto", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Mountain,
    meta: { date: "6 Agosto 2023", location: "Preturo (AQ)", type: "Visita Culturale" },
  },
  {
    id: "attivita-aquila-bici-2023",
    title: "L'Aquila in Bici",
    description: "Cicloturistica per le vie dell'Aquila",
    keywords: ["aquila", "bici", "cicloturistica", "giugno", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "17 Giugno 2023", location: "L'Aquila", type: "Cicloturistica" },
  },
  {
    id: "attivita-valle-aterno-2023",
    title: "Castelli Media Valle dell'Aterno",
    description: "Escursione ai castelli della Media Valle dell'Aterno",
    keywords: ["valle", "aterno", "castelli", "escursione", "maggio", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "21 Maggio 2023", location: "Valle dell'Aterno (AQ)", type: "Escursione" },
  },
  {
    id: "attivita-cascina-cesura-2023",
    title: "Castelli di Cascina e Cesura",
    description: "Cicloturistica ai castelli di Cascina e Cesura",
    keywords: ["cascina", "cesura", "castelli", "cicloturistica", "maggio", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Bike,
    meta: { date: "7 Maggio 2023", location: "Cascina - Cesura (AQ)", type: "Cicloturistica" },
  },
  {
    id: "attivita-rocca-san-marco-2023",
    title: "Rocca di San Marco",
    description: "Escursione alla Rocca di San Marco",
    keywords: ["rocca", "san marco", "escursione", "aprile", "2023"],
    category: "attivita",
    path: "/attivita",
    icon: Footprints,
    meta: { date: "23 Aprile 2023", location: "San Marco (AQ)", type: "Escursione" },
  },
];

// ============================================
// INDICE COMPLETO
// ============================================

export const SEARCH_INDEX: SearchItem[] = [...SEARCH_PAGES, ...SEARCH_CASTELLI, ...SEARCH_ATTIVITA];

// ============================================
// FUNZIONI DI RICERCA
// ============================================

/**
 * Normalizza una stringa per la ricerca (rimuove accenti, lowercase)
 */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[''`]/g, "'");
}

/**
 * Calcola il punteggio di rilevanza per un risultato
 */
function calculateScore(item: SearchItem, query: string): number {
  const normalizedQuery = normalizeString(query);
  const normalizedTitle = normalizeString(item.title);
  const normalizedDescription = normalizeString(item.description);
  const normalizedKeywords = item.keywords.map(normalizeString);

  let score = 0;

  // Match esatto nel titolo (massimo punteggio)
  if (normalizedTitle === normalizedQuery) {
    score += 100;
  }
  // Titolo inizia con la query
  else if (normalizedTitle.startsWith(normalizedQuery)) {
    score += 80;
  }
  // Titolo contiene la query
  else if (normalizedTitle.includes(normalizedQuery)) {
    score += 60;
  }

  // Keywords contengono la query
  for (const keyword of normalizedKeywords) {
    if (keyword === normalizedQuery) {
      score += 50;
    } else if (keyword.includes(normalizedQuery)) {
      score += 30;
    }
  }

  // Descrizione contiene la query
  if (normalizedDescription.includes(normalizedQuery)) {
    score += 20;
  }

  // Location contiene la query (per castelli e attività)
  if (item.meta?.location) {
    const normalizedLocation = normalizeString(item.meta.location);
    if (normalizedLocation.includes(normalizedQuery)) {
      score += 25;
    }
  }

  return score;
}

/**
 * Esegue la ricerca nell'indice
 */
export function searchIndex(query: string, limit: number = 10): SearchItem[] {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const normalizedQuery = normalizeString(query.trim());

  // Calcola punteggio per ogni item
  const scoredItems = SEARCH_INDEX.map((item) => ({
    item,
    score: calculateScore(item, normalizedQuery),
  }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ item }) => item);

  return scoredItems;
}

/**
 * Raggruppa i risultati per categoria
 */
export function groupByCategory(items: SearchItem[]): Record<SearchCategory, SearchItem[]> {
  const grouped: Record<SearchCategory, SearchItem[]> = {
    pages: [],
    castelli: [],
    attivita: [],
  };

  for (const item of items) {
    grouped[item.category].push(item);
  }

  return grouped;
}

/**
 * Restituisce il label per una categoria
 */
export function getCategoryLabel(category: SearchCategory): string {
  switch (category) {
    case "pages":
      return "Pagine";
    case "castelli":
      return "Castelli";
    case "attivita":
      return "Attività";
    default:
      return category;
  }
}
